import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { IconX } from "@tabler/icons";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { PERIODS, datesWords, periodDays, periodOf } from "./listParams";
import { ownerLabel } from "./listWords";

// The Cases list's filters (CMS review rania-08, rania-11, nadia-03,
// nadia-17, nadia-40, karim-16): destination, visa type, owner and the day
// submitted beside Paid and Order; on a phone one "Filters" button opens
// them all. Every active filter shows as a chip that removes it, and the
// owners show with their open and late cases.

const selectSx = { minWidth: { xs: 0, sm: 150 }, maxWidth: { sm: 220 }, "& .MuiInputBase-root": { minHeight: 40 } };
const field = (label, value, onChange, children, extra = {}) => (
  <TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={selectSx} fullWidth={extra.fullWidth} {...extra.props}>
    {children}
  </TextField>
);

// "12 open, 2 late, 3 waiting 7+ days": late is past the day promised to
// the customer (round 2, rania-10), when the server counts it; the 7+ days
// stay beside it. An older server: "12 open, 3 waiting 7+ days".
export const ownerCounts = (owner) =>
  `${owner.open} open${owner.past_due ? `, ${owner.past_due} late` : ""}${owner.late ? `, ${owner.late} waiting 7+ days` : ""}`;
// The chip's short form: "Sami 12 · 2 late · 3 7+ days".
export const ownerShort = (owner) => `${owner.open}${owner.past_due ? ` · ${owner.past_due} late` : ""}${owner.late ? ` · ${owner.late} 7+ days` : ""}`;

export function DatesDialog({ open, from, to, onClose, onApply, title = "Submitted between" }) {
  const [start, setStart] = useState(from || "");
  const [end, setEnd] = useState(to || "");
  useEffect(() => {
    if (open) {
      setStart(from || "");
      setEnd(to || "");
    }
  }, [open, from, to]);
  const wrong = start && end && start > end;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="cases-dates-title">
      <DialogTitle id="cases-dates-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField type="date" label="From" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="date" label="To (included)" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          {wrong && (
            <Typography role="alert" variant="body2" sx={{ color: "error.main" }}>
              “From” is after “To”. Pick an earlier start date.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton variant="contained" size="medium" disabled={(!start && !end) || !!wrong} onClick={() => onApply({ from: start || null, to: end || null })}>
          Show these dates
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

DatesDialog.propTypes = { open: PropTypes.bool, from: PropTypes.string, to: PropTypes.string, onClose: PropTypes.func, onApply: PropTypes.func, title: PropTypes.string };

// The controls themselves: inline on a computer, stacked in the dialog on a
// phone. set(changes) writes the address.
export function FilterControls({ view, paid, filters, sort, sortOptions, onSort, options, owners, me, set, stacked, ownersReady }) {
  const [datesOpen, setDatesOpen] = useState(false);
  const period = periodOf(filters.from, filters.to);
  const props = { fullWidth: stacked };
  const owner = filters.owner == null ? "anyone" : String(filters.owner);
  const ownerList = owners || [];
  const others = ownerList.map((o) => o.name).filter(Boolean);
  const destinations = options?.destinations || [];
  const visaTypes = options?.visa_types || [];
  return (
    <>
      {view !== "drafts" &&
        field(
          "Paid",
          paid,
          (value) => set({ paid: value === "all" ? "" : value }, { savePaid: value }),
          [
            <MenuItem key="all" value="all">
              Paid and not paid
            </MenuItem>,
            <MenuItem key="paid" value="paid">
              Paid
            </MenuItem>,
            <MenuItem key="unpaid" value="unpaid">
              Not paid yet
            </MenuItem>,
            // Builder P: the customer chose Pay later (Whish, OMT or cash).
            <MenuItem key="pay_later" value="pay_later">
              Waiting for payment (Pay later)
            </MenuItem>,
          ],
          props,
        )}
      {field(
        "Submitted",
        period || "custom",
        (value) => {
          if (value === "custom") return setDatesOpen(true);
          const days = value === "any" ? { from: null, to: null } : periodDays(value);
          return set({ from: days.from || "", to: days.to || "" });
        },
        [
          <MenuItem key="any" value="any">
            Any date
          </MenuItem>,
          ...PERIODS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          )),
          <MenuItem key="custom" value="custom" onClick={() => setDatesOpen(true)}>
            {period ? "Choose dates…" : datesWords(filters.from, filters.to).replace(/^submitted /, "")}
          </MenuItem>,
        ],
        props,
      )}
      {field(
        "Destination",
        filters.country ? String(filters.country) : "any",
        (value) => set({ country: value === "any" ? "" : value }),
        [
          <MenuItem key="any" value="any">
            Any destination
          </MenuItem>,
          ...destinations.map((d) => (
            <MenuItem key={d.id} value={String(d.id)}>
              {d.name} ({d.count})
            </MenuItem>
          )),
          // A destination from a link, before the list of them has loaded.
          ...(filters.country && !destinations.some((d) => Number(d.id) === Number(filters.country))
            ? [
                <MenuItem key="current" value={String(filters.country)}>
                  Destination #{filters.country}
                </MenuItem>,
              ]
            : []),
        ],
        props,
      )}
      {field(
        "Visa type",
        filters.visa ? String(filters.visa) : "any",
        (value) => set({ visa: value === "any" ? "" : value }),
        [
          <MenuItem key="any" value="any">
            Any visa type
          </MenuItem>,
          ...visaTypes.map((v) => (
            <MenuItem key={v.id} value={String(v.id)}>
              {v.name} ({v.count})
            </MenuItem>
          )),
          ...(filters.visa && !visaTypes.some((v) => Number(v.id) === Number(filters.visa))
            ? [
                <MenuItem key="current" value={String(filters.visa)}>
                  Visa type #{filters.visa}
                </MenuItem>,
              ]
            : []),
        ],
        props,
      )}
      {ownersReady &&
        field(
          "Owner",
          owner,
          (value) => set({ owner: value === "anyone" ? "" : value }),
          [
            <MenuItem key="anyone" value="anyone">
              Anyone
            </MenuItem>,
            ...ownerList
              .filter((o) => o.id != null)
              .map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  {o.id === me ? "Me" : ownerLabel(o.name, others)} · {ownerCounts(o)}
                </MenuItem>
              )),
            ...(me && !ownerList.some((o) => o.id === me)
              ? [
                  <MenuItem key="me" value={String(me)}>
                    Me · 0 open
                  </MenuItem>,
                ]
              : []),
            ...(filters.owner && filters.owner !== "none" && filters.owner !== me && !ownerList.some((o) => o.id === filters.owner)
              ? [
                  <MenuItem key="current" value={String(filters.owner)}>
                    Owner #{filters.owner}
                  </MenuItem>,
                ]
              : []),
            <MenuItem key="none" value="none">
              Nobody{ownerList.find((o) => o.id == null) ? ` · ${ownerCounts(ownerList.find((o) => o.id == null))}` : ""}
            </MenuItem>,
          ],
          props,
        )}
      {field(
        "Order",
        sort,
        onSort,
        sortOptions.map((x) => (
          <MenuItem key={x.key} value={x.key}>
            {x.label}
          </MenuItem>
        )),
        props,
      )}
      <DatesDialog
        open={datesOpen}
        from={filters.from}
        to={filters.to}
        onClose={() => setDatesOpen(false)}
        onApply={(days) => {
          setDatesOpen(false);
          set({ from: days.from || "", to: days.to || "" });
        }}
      />
    </>
  );
}

FilterControls.propTypes = {
  view: PropTypes.string,
  paid: PropTypes.string,
  filters: PropTypes.object.isRequired,
  sort: PropTypes.string,
  sortOptions: PropTypes.array.isRequired,
  onSort: PropTypes.func.isRequired,
  options: PropTypes.object,
  owners: PropTypes.array,
  me: PropTypes.number,
  set: PropTypes.func.isRequired,
  stacked: PropTypes.bool,
  ownersReady: PropTypes.bool,
};

// On a phone: every filter, and the one test-account switch, in a dialog.
export function FiltersDialog({ open, onClose, count, hideTest, onHideTest, ...controls }) {
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  return (
    <Dialog open={open} onClose={onClose} fullScreen={phone} maxWidth="xs" fullWidth aria-labelledby="cases-filters-title">
      <DialogTitle id="cases-filters-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        Filters{count ? ` (${count})` : ""}
        <IconButton aria-label="Close the filters" onClick={onClose} sx={iconButtonSx}>
          <IconX size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FilterControls {...controls} stacked />
          <FormControlLabel control={<Switch checked={hideTest} onChange={(e) => onHideTest(e.target.checked)} />} label="Hide test accounts" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <ActionButton variant="contained" size="medium" onClick={onClose} sx={{ width: { xs: "100%", sm: "auto" } }}>
          Show the cases
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

FiltersDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func, count: PropTypes.number, hideTest: PropTypes.bool, onHideTest: PropTypes.func };

// The active filters, each a chip that removes it, and "Clear all".
export function ActiveFilters({ chips, onClear, onClearAll }) {
  if (!chips.length) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ px: 1, pt: 1 }} role="group" aria-label="Active filters">
      {chips.map((chip) => (
        <Chip key={chip.key} label={chip.label} onDelete={() => onClear(chip.clear)} color="primary" variant="outlined" sx={{ maxWidth: "100%" }} deleteIcon={<IconX size={14} aria-label={`Remove ${chip.label}`} />} />
      ))}
      {chips.length > 1 && (
        <Box component="button" type="button" onClick={onClearAll} sx={{ p: 0.5, border: 0, bgcolor: "transparent", color: "primary.main", font: "inherit", fontWeight: 600, cursor: "pointer", textDecoration: "underline", minHeight: 32 }}>
          Clear all filters
        </Box>
      )}
    </Stack>
  );
}

ActiveFilters.propTypes = { chips: PropTypes.array.isRequired, onClear: PropTypes.func.isRequired, onClearAll: PropTypes.func.isRequired };

// Who has what (rania-08): one chip per owner with their open cases, how
// many are past the promised day and how many wait 7+ days, and Nobody.
// Pressing one shows only theirs.
export function OwnerChips({ owners, me, selected, onPick }) {
  if (!owners || !owners.length) return null;
  const others = owners.map((o) => o.name).filter(Boolean);
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ px: 1, pt: 1 }} role="group" aria-label="Who has what">
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
        Who has what:
      </Typography>
      {owners.map((o) => {
        const key = o.id == null ? "none" : o.id;
        const on = String(selected ?? "") === String(key);
        const name = o.id == null ? "Nobody" : o.id === me ? "Me" : ownerLabel(o.name, others);
        return (
          <Chip
            key={key}
            clickable
            color={on ? "primary" : "default"}
            variant={on ? "filled" : "outlined"}
            onClick={() => onPick(on ? "" : String(key))}
            aria-pressed={on}
            label={`${name} ${ownerShort(o)}`}
            title={`${o.id == null ? "Nobody" : o.name}: ${ownerCounts(o)}`}
          />
        );
      })}
    </Stack>
  );
}

OwnerChips.propTypes = { owners: PropTypes.array, me: PropTypes.number, selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), onPick: PropTypes.func.isRequired };
